import { useEffect, useMemo, useState } from "react";
import {
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
  useUpdate,
  type RaRecord,
} from "react-admin";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { calcBookingTotals } from "@/utils/booking-calculations";
import type { BookingHallLine } from "@/types/booking";
import { hasPermission } from "@/utils/permission-utils";

function flattenHalls(record: RaRecord): BookingHallLine[] {
  const halls = (record.hallBookings as RaRecord[] | undefined) ?? [];
  return halls.map((h) => ({
    ...h,
    ...((h.hall as object) ?? {}),
    ...((h.bookingPurpose as object) ?? {}),
  })) as BookingHallLine[];
}

export function HallBookingsBookingAgreedTotalEdit({ embedded = false }: { embedded?: boolean }) {
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading: saving }] = useUpdate();
  const [open, setOpen] = useState(false);
  const [agreedTotalPayable, setAgreedTotalPayable] = useState("");
  const [remarks, setRemarks] = useState("");
  const [agreedError, setAgreedError] = useState("");
  const [remarksError, setRemarksError] = useState("");

  const calculatedTotal = useMemo(() => {
    if (!record) return 0;
    return calcBookingTotals({ halls: flattenHalls(record) }).totalAmountPending;
  }, [record]);

  useEffect(() => {
    if (!open || !record) return;
    const hasAgreedValue =
      record.agreedTotalPayable !== null &&
      record.agreedTotalPayable !== undefined &&
      record.agreedTotalPayable !== "";
    setAgreedTotalPayable(
      hasAgreedValue
        ? String(Math.round(Number(record.agreedTotalPayable) || 0))
        : String(Math.round(calculatedTotal))
    );
    setRemarks(typeof record.remarks === "string" ? record.remarks : "");
    setAgreedError("");
    setRemarksError("");
  }, [open, record, calculatedTotal]);

  if (!record?.id || !hasPermission(permissions, "bookings.edit")) {
    return null;
  }
  if (record.checkedOutOn) {
    return null;
  }

  const hasOverride =
    record.agreedTotalPayable !== null &&
    record.agreedTotalPayable !== undefined &&
    record.agreedTotalPayable !== "";

  const handleSave: () => void = () => {
    const trimmedRemarks = remarks.trim();
    const n = Number(agreedTotalPayable);
    if (!Number.isFinite(n) || n < 0) {
      setAgreedError("Enter a valid agreed total");
      return;
    }
    if (!trimmedRemarks) {
      setRemarksError("Booking remarks are required when using an agreed total");
      return;
    }

    setAgreedError("");
    setRemarksError("");

    update(
      "bookings",
      {
        id: String(record.id),
        data: {
          agreedTotalPayable: Math.round(Number(agreedTotalPayable) || 0),
          remarks: trimmedRemarks || null,
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          notify("Agreed contribution updated", { type: "success" });
          refresh();
          setOpen(false);
        },
        onError: (err) => {
          notify(err instanceof Error ? err.message : "Could not update agreed contribution", {
            type: "error",
          });
        },
      }
    );
  };

  const handleClearOverride: () => void = () => {
    update(
      "bookings",
      {
        id: String(record.id),
        data: {
          agreedTotalPayable: null,
          remarks: remarks.trim() || null,
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          notify("Using calculated total now", { type: "success" });
          refresh();
          setOpen(false);
        },
        onError: (err) => {
          notify(err instanceof Error ? err.message : "Could not clear agreed contribution", {
            type: "error",
          });
        },
      }
    );
  };

  return (
    <Box sx={{ mb: 0, mt: embedded ? 1 : 0, maxWidth: embedded ? "none" : 560 }}>
      {!embedded ? (
        <Typography variant="subtitle1" gutterBottom>
          Agreed contribution total
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
          {hasOverride ? "Update agreed total" : "Set agreed total"}
        </Button>
        {hasOverride ? (
          <Typography variant="caption" color="text.secondary">
            Using agreed total
          </Typography>
        ) : null}
      </Box>

      <Dialog
        open={open}
        onClose={() => {
          if (!saving) setOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agreed contribution total</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Override the hall-line total when a different agreed amount applies (e.g. khidmat rate).
            Calculated hall total: ₹{calculatedTotal}.
          </Typography>
          <TextField
            label="Agreed total payable"
            type="number"
            value={agreedTotalPayable}
            onChange={(e) => {
              setAgreedTotalPayable(e.target.value);
              if (agreedError) setAgreedError("");
            }}
            fullWidth
            margin="dense"
            error={Boolean(agreedError)}
            helperText={agreedError}
            disabled={saving}
          />
          <TextField
            label="Booking remarks"
            placeholder="Referred by name, phone number, etc."
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            value={remarks}
            onChange={(e) => {
              setRemarks(e.target.value);
              if (remarksError) setRemarksError("");
            }}
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            error={Boolean(remarksError)}
            helperText={remarksError || "Required when setting an agreed total."}
            disabled={saving}
          />
          {(() => {
            const agreed = Math.round(Number(agreedTotalPayable) || 0);
            const waived = calculatedTotal - agreed;
            if (waived <= 0) return null;
            return (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Taking ₹{waived} less than calculated hall total (₹{calculatedTotal}).
              </Typography>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          {hasOverride ? (
            <Button onClick={handleClearOverride} color="warning" disabled={saving}>
              {saving ? "Updating..." : "Use calculated total"}
            </Button>
          ) : null}
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HallBookingsBookingAgreedTotalEdit;
